import { FileItemActionType } from '@/Components/AttachedFilesPopover/PopoverFileItemAction';
import { NoteViewController } from '@/Components/NoteView/Controller/NoteViewController';
import { AppPaneId } from '@/Components/Panes/AppPaneMetadata';
import { createLinkFromItem } from '@/Utils/Items/Search/createLinkFromItem';
import { ApplicationEvent, ContentType, FileItem, naturalSort, PrefKey, SNNote, SNTag, isFile, isNote, isTag, PrefDefaults, } from '@standardnotes/snjs';
import { action, computed, makeObservable, observable } from 'mobx';
import { AbstractViewController } from './Abstract/AbstractViewController';
import { CrossControllerEvent } from './CrossControllerEvent';
export class LinkingController extends AbstractViewController {
    constructor(itemListController, filesController, subscriptionController, navigationController, featuresController, itemControllerGroup, vaultDisplayService, preferences, items, mutator, sync, vaults, eventBus) {
        super(eventBus);
        this.itemListController = itemListController;
        this.filesController = filesController;
        this.subscriptionController = subscriptionController;
        this.navigationController = navigationController;
        this.featuresController = featuresController;
        this.itemControllerGroup = itemControllerGroup;
        this.vaultDisplayService = vaultDisplayService;
        this.preferences = preferences;
        this.items = items;
        this.mutator = mutator;
        this.sync = sync;
        this.vaults = vaults;
        this.isLinkingPanelOpen = false;
        this.setIsLinkingPanelOpen = (open) => {
            this.isLinkingPanelOpen = open;
        };
        this.getFilesLinksForItem = (item) => {
            if (!item || this.items.isTemplateItem(item)) {
                return {
                    filesLinkedToItem: [],
                    filesLinkingToItem: [],
                };
            }
            const referencesOfItem = naturalSort(this.items.referencesForItem(item).filter(isFile), 'title');
            const referencingItem = naturalSort(this.items.itemsReferencingItem(item).filter(isFile), 'title');
            if (item.content_type === ContentType.TYPES.File) {
                return {
                    filesLinkedToItem: referencesOfItem.map((item) => createLinkFromItem(item, 'linked')),
                    filesLinkingToItem: referencingItem.map((item) => createLinkFromItem(item, 'linked-by')),
                };
            }
            else {
                return {
                    filesLinkedToItem: referencingItem.map((item) => createLinkFromItem(item, 'linked')),
                    filesLinkingToItem: referencesOfItem.map((item) => createLinkFromItem(item, 'linked-by')),
                };
            }
        };
        this.getLinkedTagsForItem = (item) => {
            if (!item) {
                return;
            }
            return this.items.getSortedTagsForItem(item).map((tag) => createLinkFromItem(tag, 'linked'));
        };
        this.getLinkedNotesForItem = (item) => {
            if (!item || this.items.isTemplateItem(item)) {
                return [];
            }
            return naturalSort(this.items.referencesForItem(item).filter(isNote), 'title').map((item) => createLinkFromItem(item, 'linked'));
        };
        this.getNotesLinkingToItem = (item) => {
            if (!item) {
                return [];
            }
            return naturalSort(this.items.itemsReferencingItem(item).filter(isNote), 'title').map((item) => createLinkFromItem(item, 'linked-by'));
        };
        this.activateItem = async (item) => {
            this.setIsLinkingPanelOpen(false);
            if (item instanceof SNTag) {
                await this.navigationController.setSelectedTag(item, 'all');
                return AppPaneId.Items;
            }
            else if (item instanceof SNNote) {
                await this.navigationController.selectHomeNavigationView();
                const { didSelect } = await this.itemListController.selectItem(item.uuid, true);
                if (didSelect) {
                    return AppPaneId.Editor;
                }
            }
            else if (item instanceof FileItem) {
                await this.filesController.handleFileAction({
                    type: FileItemActionType.PreviewFile,
                    payload: {
                        file: item,
                        otherFiles: [],
                    },
                });
            }
            return undefined;
        };
        this.unlinkItems = async (item, itemToUnlink) => {
            try {
                await this.mutator.unlinkItems(item, itemToUnlink);
            }
            catch (error) {
                console.error(error);
            }
            void this.sync.sync();
        };
        this.unlinkItemFromSelectedItem = async (itemToUnlink) => {
            const selectedItem = this.itemListController.firstSelectedItem;
            if (!selectedItem) {
                return;
            }
            void this.unlinkItems(selectedItem, itemToUnlink);
        };
        this.ensureActiveItemIsInserted = async () => {
            const activeItemController = this.itemListController.getActiveItemController();
            if (activeItemController instanceof NoteViewController && activeItemController.isTemplateNote) {
                await activeItemController.insertTemplatedNote();
            }
        };
        this.linkItems = async (item, itemToLink, sync = true) => {
            var _a;
            const linkNoteAndFile = async (note, file) => {
                const updatedFile = await this.mutator.associateFileWithNote(file, note);
                if (this.featuresController.isVaultsEnabled()) {
                    if (updatedFile) {
                        const noteVault = this.vaults.getItemVault(note);
                        const fileVault = this.vaults.getItemVault(updatedFile);
                        if (noteVault && !fileVault) {
                            const result = await this.vaults.moveItemToVault(noteVault, file);
                            if (result.isFailed()) {
                                console.error(result.getError());
                            }
                        }
                    }
                }
            };
            const linkFileAndFile = async (file1, file2) => {
                await this.mutator.linkFileToFile(file1, file2);
            };
            const linkNoteToNote = async (note1, note2) => {
                await this.mutator.linkNoteToNote(note1, note2);
            };
            const linkTagToNote = async (tag, note) => {
                await this.addTagToItem(tag, note, sync);
            };
            const linkTagToFile = async (tag, file) => {
                await this.addTagToItem(tag, file, sync);
            };
            if (isNote(item)) {
                if (isNote(itemToLink) && !this.isEntitledToNoteLinking) {
                    void this.publishCrossControllerEventSync(CrossControllerEvent.DisplayPremiumModal, {
                        featureName: 'Note linking',
                    });
                    return;
                }
                if (item.uuid === ((_a = this.activeItem) === null || _a === void 0 ? void 0 : _a.uuid)) {
                    await this.ensureActiveItemIsInserted();
                }
                if (isFile(itemToLink)) {
                    await linkNoteAndFile(item, itemToLink);
                }
                else if (isNote(itemToLink)) {
                    await linkNoteToNote(item, itemToLink);
                }
                else if (isTag(itemToLink)) {
                    await linkTagToNote(itemToLink, item);
                }
                else {
                    throw Error('Invalid item type');
                }
            }
            else if (isFile(item)) {
                if (isNote(itemToLink)) {
                    await linkNoteAndFile(itemToLink, item);
                }
                else if (isFile(itemToLink)) {
                    await linkFileAndFile(item, itemToLink);
                }
                else if (isTag(itemToLink)) {
                    await linkTagToFile(itemToLink, item);
                }
                else {
                    throw Error('Invalid item to link');
                }
            }
            else {
                throw new Error('First item must be a note or file');
            }
            if (sync) {
                void this.sync.sync();
            }
        };
        this.linkItemToSelectedItem = async (itemToLink) => {
            const cannotLinkItem = !this.isEntitledToNoteLinking && itemToLink instanceof SNNote;
            if (cannotLinkItem) {
                void this.publishCrossControllerEventSync(CrossControllerEvent.DisplayPremiumModal, {
                    featureName: 'Note linking',
                });
                return false;
            }
            await this.ensureActiveItemIsInserted();
            const activeItem = this.activeItem;
            if (!activeItem) {
                return false;
            }
            await this.linkItems(activeItem, itemToLink);
            return true;
        };
        this.createAndAddNewTag = async (title) => {
            await this.ensureActiveItemIsInserted();
            const vault = this.vaultDisplayService.exclusivelyShownVault;
            const newTag = await this.mutator.findOrCreateTag(title, vault);
            const activeItem = this.activeItem;
            if (activeItem) {
                const itemVault = this.vaults.getItemVault(activeItem);
                if (itemVault) {
                    const movedTagOrError = await this.vaults.moveItemToVault(itemVault, newTag);
                    if (movedTagOrError.isFailed()) {
                        throw new Error(`Failed to move tag to item vault: ${movedTagOrError.getError()}`);
                    }
                    const movedTag = movedTagOrError.getValue();
                    await this.addTagToItem(movedTag, activeItem);
                }
                else {
                    await this.addTagToItem(newTag, activeItem);
                }
            }
            return newTag;
        };
        this.addTagToItem = async (tag, item, sync = true) => {
            if (item instanceof SNNote) {
                await this.mutator.addTagToNote(item, tag, this.shouldLinkToParentFolders);
            }
            else if (item instanceof FileItem) {
                await this.mutator.addTagToFile(item, tag, this.shouldLinkToParentFolders);
            }
            if (sync) {
                this.sync.sync().catch(console.error);
            }
        };
        makeObservable(this, {
            isLinkingPanelOpen: observable,
            isEntitledToNoteLinking: computed,
            setIsLinkingPanelOpen: action,
        });
        this.shouldLinkToParentFolders = preferences.getValue(PrefKey.NoteAddToParentFolders, PrefDefaults[PrefKey.NoteAddToParentFolders]);
        eventBus.addEventHandler(this, ApplicationEvent.PreferencesChanged);
    }
    handleEvent(event) {
        switch (event.type) {
            case ApplicationEvent.PreferencesChanged: {
                this.shouldLinkToParentFolders = this.preferences.getValue(PrefKey.NoteAddToParentFolders, PrefDefaults[PrefKey.NoteAddToParentFolders]);
                break;
            }
        }
        return Promise.resolve();
    }
    get isEntitledToNoteLinking() {
        return this.subscriptionController.hasFirstPartyOnlineOrOfflineSubscription();
    }
    get activeItem() {
        var _a;
        return (_a = this.itemControllerGroup.activeItemViewController) === null || _a === void 0 ? void 0 : _a.item;
    }
}
